import { ActionGroupTypes, ActionStatusTypes } from "../../../enum/ActionStatusTypes";
import * as logger from "../../../logger";
import { getNodeById } from "../../../nodeSelector";
import * as nodeUtils from "../../../utils/nodeUtils";
import * as pathUtils from "../../../utils/pathUtils";
import * as systemUtils from "../../../utils/systemUtils";
import { ACTION_PREFIX_PARAMETER } from "./abstract/parameterAbstract";
import { detachParameterLogic } from "./detachParameter";
import { getParameterReferences, processParameterReferences } from "./helpers/parameterHelper";
import { moveParameterFlatLogic } from "./moveParameterFlat";

const _action = `${ACTION_PREFIX_PARAMETER}_remove`;
const _header = `Remove parameter`;

export const removeParameter = (root, id, keep = false, remove = false) => {
  const _payload = removeParameterLogic(root, id, keep, remove);

  logger.logAction(_action, _payload, ACTION_PREFIX_PARAMETER);

  return { root, info: _payload };
};

//--------------------------------------------------------------------------------

export const removeParameterLogic = (root, id, keep, remove) => {
  const { data: _node, index: _index } = getNodeById(root, id);

  if (!_node) {
    throw new Error("Parameter to remove not found");
  }
  const { data: _nodeParent } = getNodeById(root, pathUtils.getIdParent(id));

  const _nodeBackup = systemUtils.createDeepCopy(_node);
  const _references = getParameterReferences(root, _node);

  preserveNodes(root, _node, _nodeParent, _index, keep);

  handleReferences(root, _references, remove);

  removeNode(_index, _nodeParent);

  return generatePayload(_nodeBackup, _index, _nodeParent, remove, _references.description);
};

const handleReferences = (root, references, remove) => {
  const funTarget = (e) => {
    nodeUtils.removeIdReference(e.dst, nodeUtils.getID(e.src));
  };

  const funReference = (e) => {
    if (remove) {
      removeParameterLogic(root, nodeUtils.getID(e.src), true);
    } else {
      detachParameterLogic(root, nodeUtils.getID(e.src));
    }
  };

  processParameterReferences(references, funTarget, funTarget, funReference, funReference);
};

const preserveNodes = (root, node, nodeParent, index, keep) => {
  const _preserved = [];

  if (keep) {
    const _children = nodeUtils.getParameters(node);

    const fun = (e) => {
      _preserved.push(nodeUtils.getUUID(e));

      const _index = index + _preserved.length;
      moveParameterFlatLogic(root, nodeUtils.getID(e), nodeUtils.getID(nodeParent), _index);
    };

    _children.forEach(fun);
  }

  return _preserved;
};

const removeNode = (index, nodeParent) => {
  nodeUtils.modifyParameters(nodeParent, (e) => e.splice(index, 1), false);
};

//--------------------------------------------------------------------------------

const generatePayload = (node, index, nodeParent, remove, uuidReferences) => {
  const _payload = {
    group: ActionGroupTypes.DELETE,
    status: nodeUtils.getName(node) ? ActionStatusTypes.SUCCESS : ActionStatusTypes.INDIFFERENT,
    action: _action,
    time: systemUtils.getTimestamp(),
    header: _header,
    description: generatePayloadDescription(node, nodeParent),
    node: node,
    uuid: nodeUtils.getUUID(node),
    uuidParent: nodeUtils.getUUID(nodeParent),
    index: index,
  };

  if (remove === true) {
    _payload.effect = "remove";
  } else if (remove === false) {
    _payload.effect = "detach";
  }

  return { ..._payload, ...uuidReferences };
};

const generatePayloadDescription = (node, nodeParent) => {

  if (nodeUtils.isRoot(nodeParent)) {
    return `Delete top global parameter '${nodeUtils.getName(node)}'.`;
  }

  if (nodeUtils.isParameterGlobal(nodeParent)) {
    return `Delete nested global parameter '${nodeUtils.getName(node)}'.`;
  }

  if (nodeUtils.isFunction(nodeParent)) {
    return `Delete parameter '${nodeUtils.getName(node)}'.`;
  }

  return `Delete nested parameter'${nodeUtils.getName(node)}'.`;
}